/**
 * The runner behind `lib/autoEnquiry.ts`: watches the inbox list the Mail screen already polls,
 * and for each NEW RFQ-looking mail reads it (the same extraction the create bar uses, with its
 * OCR and LLM fallbacks) and creates the enquiry when the mail resolves everything the server
 * requires — see the decision module for the two rules. One at a time, never re-tried in the
 * same session, and never for the mail that was on screen when the page opened.
 *
 * Two people with the page open would both see the new mail. A fresh `get_enquiries` read just
 * before the create catches the other's enquiry when it landed first; the remaining window is
 * one round trip, and the ✓ badge on the row shows both enquiries if it ever closes on both.
 */
import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { callGet, callPost } from './bosun'
import { createEnquiryFlow } from './createEnquiryFlow'
import { runAndDeliver } from './useMailExtraction'
import { MAIL_POLL_MS, toMailDetail, type WireMailDetail } from './useMail'
import { todayLocalDate } from './useEnquiryMutations'
import { siteDateTime } from '../markSent'
import type { WireEnquirySummary } from './useEnquiries'
import type { WireList } from './wire'
import { AUTO_ENQUIRY_MARK, autoEnquiryCandidates, planAutoEnquiry, type AutoEnquiryContext } from '../autoEnquiry'
import { classifyEnquiryType } from '../enquiryTypeClassifier'
import { getResource } from './frappe'
import { useQuery } from '@tanstack/react-query'
import { str } from './wire'
import type { ExtractionResult } from './mailExtractionState'
import type { EnquirySummary, MailMessage } from '../types'

export interface AutoEnquiryEvent {
  mail: MailMessage
  enquiryId?: string
  skipped?: string
  error?: string
}

export function useAutoEnquiries(input: {
  /** The inbox as listed — the poll keeps it fresh; a new id here is a new mail. */
  mails: readonly MailMessage[]
  /** `true` once the list has actually loaded — the ids on screen then become the watermark. */
  loaded: boolean
  processedByMail: ReadonlyMap<string, EnquirySummary[]>
  /** Everything but the type, which is classified per mail from its items. */
  context: Omit<AutoEnquiryContext, 'enquiryType'> | null
  /** The server's `Enquiry Type` master — what the classifier may answer. */
  enquiryTypes: readonly string[]
  enabled: boolean
  onEvent: (event: AutoEnquiryEvent) => void
}) {
  const queryClient = useQueryClient()
  const seen = useRef<Set<string> | null>(null)
  const openedAt = useRef(siteDateTime(new Date(Date.now() - 5 * 60_000)))
  const attempted = useRef(new Set<string>())
  const running = useRef(false)
  const latest = useRef(input)
  useEffect(() => {
    latest.current = input
  })

  useEffect(() => {
    const { mails, loaded, processedByMail, context, enabled } = input
    if (!loaded) return
    // The watermark: whatever was on screen when the page opened is the backlog, not "new".
    if (seen.current === null) {
      seen.current = new Set(mails.map((m) => m.id))
      return
    }
    if (!enabled || !context || running.current) return
    const candidates = autoEnquiryCandidates(mails, { seen: seen.current, processed: processedByMail, attempted: attempted.current, since: openedAt.current })
    if (candidates.length === 0) return

    running.current = true
    void (async () => {
      try {
        for (const listed of candidates) {
          attempted.current.add(listed.id)
          const { onEvent } = latest.current
          try {
            const mail = toMailDetail(await callGet<WireMailDetail>('mail', 'get_email', { name: listed.id }))
            const result = await new Promise<ExtractionResult>((resolve, reject) => {
              void runAndDeliver(mail, { queryClient }, { onSuccess: resolve, onError: (e) => reject(new Error(e.message)) })
            })
            // The type from the items (LLM, keyword fallback) — never a hard-coded default.
            const enquiryType = await classifyEnquiryType(result.items.map((i) => i.description), latest.current.enquiryTypes)
            const plan = planAutoEnquiry(mail, result, { ...(latest.current.context ?? context), enquiryType })
            if (!plan.ok) {
              onEvent({ mail, skipped: plan.reason })
              continue
            }
            // Someone else's page may have created it in the meantime.
            const fresh = await callGet<WireList<WireEnquirySummary>>('enquiry', 'get_enquiries', { limit: 200, offset: 0 })
            const already = (fresh.data ?? []).find((e) => e.mail_id === mail.id)
            if (already) {
              onEvent({ mail, skipped: `already ${String(already.name)}` })
              continue
            }
            const created = await createEnquiryFlow(plan.header, todayLocalDate(), { callGet, callPost })
            void queryClient.invalidateQueries({ queryKey: ['enquiries'] })
            void queryClient.invalidateQueries({ queryKey: ['board-replies'] })
            onEvent({ mail, enquiryId: created.enquiry.id, ...(created.warning ? { error: created.warning } : {}) })
          } catch (error) {
            onEvent({ mail: listed, error: error instanceof Error ? error.message : String(error) })
          }
        }
      } finally {
        running.current = false
      }
    })()
    // `input` changes on every render; the list identity and the load flag are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.mails, input.loaded, input.processedByMail, input.enabled, input.context])
}

/** The enquiries Bosun created without a person — `AUTO_ENQUIRY_MARK` on `enquiry_remarks`. */
export function useAutoCreatedEnquiries() {
  return useQuery({
    queryKey: ['enquiries', 'auto-created'] as const,
    staleTime: 30_000,
    // The Waves watcher creates these with nobody logged in — poll, or the row never turns.
    refetchInterval: MAIL_POLL_MS,
    queryFn: async () => {
      const page = await getResource<Record<string, unknown>>('Enquiry', {
        fields: ['name'],
        filters: [['enquiry_remarks', 'like', `${AUTO_ENQUIRY_MARK}%`]],
        limit: 0,
      })
      return new Set(page.data.map((r) => str(r.name)))
    },
  })
}

/** The vessel master, code + name, for `pickVessel` — 2,261 rows on the bench, read once. */
export function useVesselList() {
  return useQuery({
    queryKey: ['masters', 'vessels', 'all'] as const,
    staleTime: 30 * 60_000,
    queryFn: async () => {
      const page = await getResource<Record<string, unknown>>('Vessel', { fields: ['name', 'vessel_name'], limit: 0 })
      return page.data.map((r) => ({ code: str(r.name), name: str(r.vessel_name) })).filter((v) => v.code !== '')
    },
  })
}

/** The port master — 87 names on the bench. */
export function usePortList() {
  return useQuery({
    queryKey: ['masters', 'ports', 'all'] as const,
    staleTime: 30 * 60_000,
    queryFn: async () => {
      const page = await getResource<Record<string, unknown>>('Port', { fields: ['name'], limit: 0 })
      return page.data.map((r) => str(r.name)).filter((n) => n !== '')
    },
  })
}
